import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Divider, useAuthenticator } from '@aws-amplify/ui-react';
import PageBase from '@components/pages/PageBase';
import Stepper from '@components/parts/Stepper';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import dayjs from 'dayjs';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { getGroupDetailApi, getReservationMasterDataApi, getSectionInfoListApi } from '@apis/index';
import { LoadingState } from '@atom/LoadingState';
import { toNumber } from '@utils/NumberUtils';
import { validateEmail, validateNumber } from '@utils/ValidationUtils';

import {
  GetGroupDetailQueryVariables,
  GetReservationMasterDataQueryVariables,
  GetSectionInfoListQueryVariables,
  getGroupDetailType,
  getReservationMasterDataType,
  getSectionInfoListType,
  usageDetailListType
} from '../../../API';
import { FacilityState } from '../../../atom/FacilityState';
import { MessageDialogState } from '../../../atom/DialogState';
import { ReservationState } from '../../../atom/ReservationState';
import { GraphQLErrorResponse } from '../commonType';

const UsagePurposeRegisterPage = () => {
  /* 共通State */
  const setMessageDialogState = useSetRecoilState(MessageDialogState);
  const setLoading = useSetRecoilState(LoadingState);
  const navigate = useNavigate();
  /* 認証情報 */
  const { user, signOut } = useAuthenticator((context) => [context.user]);
  const [groupId, setGroupId] = useState<string>('99999');
  const [isLoginUser, setIsLoginUser] = useState<boolean>(false);

  useEffect(() => {
    // ログインユーザーかどうか判定する
    if (user !== undefined) {
      // isLoginUser=true
      setIsLoginUser(true);
      setGroupId(user.username);
    }
  }, [user]);

  const facility = useRecoilValue(FacilityState);
  const [reservation, setReservation] = useRecoilState(ReservationState);

  const [searchParams] = useSearchParams();

  const [reservationMasterData, setReservationMasterData] = useState<getReservationMasterDataType[]>([]);
  const [contentOptions, setContentOptions] = useState<usageDetailListType[]>([]);
  const [sectionInfoList, setSectionInfoList] = useState<getSectionInfoListType[]>([]);
  const [groupDetailData, setGroupDetailData] = useState<getGroupDetailType>();

  // クエリストリング取得
  const facilityId = searchParams.get('facilityId') ?? '';
  const title = searchParams.get('title') ?? '';
  const areaId = searchParams.get('areaId') ?? '';
  const date = searchParams.get('date') ?? '';
  const dispDate = dayjs(date).format('YYYY年M月D日(dd)');

  const dispStartTime = searchParams.get('startTime') ?? '';
  const dispEndTime = searchParams.get('endTime') ?? '';
  const sectionIdList = searchParams.get('sectionList')?.split(',') ?? [];
  const sectionNameList = sectionInfoList.map((section) => section.sectionName);
  const dispSectionName = sectionNameList.join(',');
  // 日付と時刻を結合してdayjsオブジェクトを作成
  const start = dayjs(`${date}T${dispStartTime}`);
  const end = dayjs(`${date}T${dispEndTime}`);
  const reservedStartTime = start.format('YYYY-MM-DDTHH:mm:ss+09:00');
  const reservedEndTime = end.format('YYYY-MM-DDTHH:mm:ss+09:00');
  const depositDate = dayjs().add(3, 'day').format('YYYY年M月D日(dd)');

  // 区画使用料金を算出する
  const calcFacilityFee = (List: getSectionInfoListType[], sTime: string, eTime: string) => {
    // 開始時間と終了時間の差(分)を計算する
    const startTime = dayjs(sTime, 'HH:mm');
    const endTime = dayjs(eTime, 'HH:mm');
    const diff: number = endTime.diff(startTime, 'minute');

    const facilityFee = List.reduce((sum, current) => {
      let itemFee = 0;
      switch (current.usageTimeFlag) {
        case 1: // 30分あたり
          itemFee = current.fee * Math.round(diff / 30);
          break;
        case 2: // 1時間あたり
          itemFee = current.fee * Math.round(diff / 60);
          break;
        case 3: // 1日あたり(利用時間に関わらず1日分の料金)
          itemFee = current.fee;
          break;
        default:
          break;
      }      
      return sum + itemFee;
    }, 0);
    return facilityFee;
  };

  const facilityFee = calcFacilityFee(sectionInfoList, dispStartTime, dispEndTime);

  // 利用目的
  const [usagePurpose, setUsagePurpose] = useState<string>(reservation.usagePurpose);
  // 利用種別
  const [usageType, setUsageType] = useState<string>('');
  // 内容
  const [usageDetails, setUsageDetails] = useState<string>('');
  // 利用団体名
  const [groupName, setGroupName] = useState<string>(reservation.groupName);
  // 代表者氏名
  const [representativeName, setRepresentativeName] = useState<string>(reservation.personInCharge);
  // メールアドレス
  const [mailAddress, setMailAddress] = useState<string>(reservation.email);
  const [mailAddressError, setMailAddressError] = useState<boolean>(false);
  // 電話番号
  const [phoneNumber, setPhoneNumber] = useState<string>(reservation.tel);
  const [phoneNumberError, setPhoneNumberError] = useState<boolean>(false);
  // 利用内容
  const [usageContent, setUsageContent] = useState<string>(reservation.usageContents);
  // 入場料
  const [entranceFee, setEntranceFee] = useState<boolean>(reservation.isCollectFee);
  // 営利目的の利用
  const [commercial, setCommercial] = useState<boolean>(reservation.isCommercialActivity);
  // 小・中学生
  const [child, setChild] = useState<number>(reservation.numberOfElementaryMiddleSchoolStudents);
  // 高校生
  const [middle, setMiddle] = useState<number>(reservation.numberOfElementaryHighSchoolStudents);
  // 一般
  const [general, setGeneral] = useState<number>(reservation.numberOfOrdinaryPerson);
  // 合計
  const [sum, setSum] = useState<number>(reservation.numberOfTotalPeople);
  // 観覧者
  const [spectator, setSpectator] = useState<number>(reservation.numberOfSpectator);

  // 開始時間
  const [startTime, setStartTime] = useState<string>(reservation.electricityUtilizationStartTime);
  // 終了時間
  const [endTime, setEndTime] = useState<string>(reservation.electricityUtilizationEndTime);
  // 電気利用
  const [usageElectric, setUsageElectric] = useState<boolean>(reservation.isElectricityUtilization);

  useEffect(() => {
    setSum(child + middle + general);
  }, [child, middle, general]);

  // 画面遷移時処理
  const confirm = (path: string) => {
    setReservation({
      ...reservation,
      facilityId,
      areaId,
      sectionIdList,
      sectionNameList,
      reservedStartTime,
      reservedEndTime,
      usagePurpose,
      usageType,
      usageDetails,
      personInCharge: representativeName,
      email: mailAddress,
      tel: phoneNumber,
      usageContents: usageContent,
      numberOfTotalPeople: sum,
      numberOfElementaryMiddleSchoolStudents: child,
      numberOfElementaryHighSchoolStudents: middle,
      numberOfOrdinaryPerson: general,
      numberOfSpectator: spectator,
      isCollectFee: entranceFee,
      isCommercialActivity: commercial,
      isElectricityUtilization: usageElectric,
      electricityUtilizationStartTime: startTime,
      electricityUtilizationEndTime: endTime,
      groupName,
      areaName: title,
      depositDate,
      facilityFee,
      groupId,
      totalFee: facilityFee
    });
    navigate(
      `/c005/${path}?facilityId=${facilityId}&areaId=${areaId}&date=${date}&title=${title}&startTime=${dispStartTime}&endTime=${dispEndTime}&sectionList=${sectionIdList}`
    );
  };

  // 検索処理
  const getSectionInfoListData = async () => {
    setLoading(true);
    try {
      const input: GetSectionInfoListQueryVariables = {
        input: {
          facilityId,
          areaId,
          sectionIdList
        }
      };
      const result = await getSectionInfoListApi(input);
      setSectionInfoList(result);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);

      let message = '時間をおいてから再度お試しください。解決できない場合は管理者へお問い合わせください。';
      if ('errors' in (error as GraphQLErrorResponse) && (error as GraphQLErrorResponse).errors.length !== 0) {
        message = (error as GraphQLErrorResponse).errors[0].message;
      }
      setMessageDialogState({
        open: true,
        messageData: {
          title: 'エラーが発生しました。',
          message
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const getMasterData = async () => {
    setLoading(true);
    try {
      const input: GetReservationMasterDataQueryVariables = {
        input: {
          facilityId
        }
      };

      const result = await getReservationMasterDataApi(input);
      setReservationMasterData(result);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);

      let message = '時間をおいてから再度お試しください。解決できない場合は管理者へお問い合わせください。';
      if ('errors' in (error as GraphQLErrorResponse) && (error as GraphQLErrorResponse).errors.length !== 0) {
        message = (error as GraphQLErrorResponse).errors[0].message;
      }
      setMessageDialogState({
        open: true,
        messageData: {
          title: 'エラーが発生しました。',
          message
        }
      });
    } finally {
      setLoading(false);
    }
  };

  /* データ取得 */
  const getData = async () => {
    setLoading(true);
    // 団体情報取得
    // API呼び出し
    try {
      const input: GetGroupDetailQueryVariables = {
        input: {
          facilityId,
          groupId: groupId || ''
        }
      };

      // データ取得
      const result = await getGroupDetailApi(input);
      setGroupDetailData(result);
      if (groupId !== '99999') {
        setGroupName(result.groupName);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);

      let message = '時間をおいてから再度お試しください。解決できない場合は管理者へお問い合わせください。';
      if ('errors' in (error as GraphQLErrorResponse) && (error as GraphQLErrorResponse).errors.length !== 0) {
        message = (error as GraphQLErrorResponse).errors[0].message;
      }
      setMessageDialogState({
        open: true,
        messageData: {
          title: 'エラーが発生しました。',
          message
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const updateContentOptions = (newUsageType: string) => {
    // 利用種別により、内容の選択肢を変更する
    const matchedData = reservationMasterData.find((item) => item.usageType === newUsageType);
    const details: any = matchedData ? matchedData.usageDetailList : [];

    // 内容のオプションを更新
    setContentOptions(details);
  };

  /* 初期処理 */
  useEffect(() => {
    getMasterData();
    getSectionInfoListData();
    if (groupId !== '') {
      getData();
    }
  }, []);
  /* 初期処理 */
  useEffect(() => {
    if (groupId !== '') {
      getData();
    }
  }, [groupId]);
  /* マスターデータ更新時に設定内容を復元 */
  useEffect(() => {
    setUsageType(reservation.usageType);
    updateContentOptions(reservation.usageType);
  }, [reservationMasterData]);

  /* 内容の選択肢更新時に内容を設定 */
  useEffect(() => {
    setUsageDetails(reservation.usageDetails);
  }, [contentOptions]);

  return (
    <PageBase title="利用目的登録" isButton={false}>
      <Box sx={{ mr: 1, ml: 1 }}>
        <Grid container spacing={2} sx={{ mt: '15px' }}>
          <Grid xs={8}>
            <Typography>
              {dispDate} {dispStartTime}-{dispEndTime}
              <br />
              {title} {dispSectionName}
            </Typography>
          </Grid>
          <Grid xs={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/c001/weeklyschedule/?facilityId=${facilityId}&areaId=${areaId}&title=${title}`)}
            >
              日付再選択
            </Button>
          </Grid>
          <Grid xs={12}>
            <Stepper activeStep={0} />
          </Grid>
          <Grid xs={12}>
            <TextField
              id="usagePurpose"
              label="利用目的(大会名等)"
              value={usagePurpose}
              inputProps={{
                maxLength: 20
              }}
              sx={{ width: '100%', textAlign: 'left' }}
              InputLabelProps={{
                shrink: true
              }}
              onChange={(event) => {
                setUsagePurpose(event.target.value);
              }}
            />
          </Grid>
          <Grid xs={12}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-autowidth-label">利用種別</InputLabel>
              <Select
                value={usageType}
                label="利用種別"
                fullWidth
                onChange={(event) => {
                  const newUsageType = event.target.value;
                  setUsageType(newUsageType);
                  // 内容のオプションを更新
                  updateContentOptions(newUsageType);
                }}
              >
                {reservationMasterData.map((item) => (
                  <MenuItem key={item.usageType} value={item.usageType}>
                    {item.usageType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={12}>
            <Autocomplete
              freeSolo
              value={usageDetails}
              onInputChange={(event, newValue) => {
                setUsageDetails(newValue);
              }}
              options={contentOptions.map((item) => item.usageDetail)}
              renderInput={(params) => (
                <TextField
                  label="内容"
                  fullWidth
                  InputLabelProps={params.InputLabelProps}
                  // 大文字と小文字で区別されているものの、eslintでは同一とみなされてしまうため除外
                  // eslint-disable-next-line react/jsx-no-duplicate-props
                  inputProps={{ ...params.inputProps, maxLength: 20 }} InputProps={params.InputProps}
                />
              )}
            />
          </Grid>
          {/* TODO グレーアウト */}
          <Grid xs={12}>
            <TextField
              id="groupName"
              label="利用団体名"
              value={groupName}
              disabled={isLoginUser}
              inputProps={{
                maxLength: 20
              }}
              sx={{ width: '100%', textAlign: 'left' }}
              InputLabelProps={{
                shrink: true
              }}
              onChange={(event) => {
                setGroupName(event.target.value);
              }}
            />
          </Grid>
          <Grid xs={12}>
            <TextField
              id="representativeName"
              label="使用責任者"
              value={representativeName}
              sx={{ width: '100%', textAlign: 'left' }}
              InputLabelProps={{
                shrink: true
              }}
              inputProps={{
                maxLength: 20
              }}
              onChange={(event) => {
                setRepresentativeName(event.target.value);
              }}
            />
          </Grid>
          {/* TODO バリデーションチェック */}
          <Grid xs={12}>
            <TextField
              id="mailAddress"
              label="メールアドレス"
              value={mailAddress}
              sx={{ width: '100%', textAlign: 'left' }}
              InputLabelProps={{
                shrink: true
              }}
              inputProps={{
                maxLength: 50
              }}
              error={mailAddressError}
              helperText={mailAddressError ? 'メールアドレスの形式で入力してください' : ''}
              onChange={(event) => {
                setMailAddress(event.target.value);
                setMailAddressError(!validateEmail(event.target.value));
              }}
            />
          </Grid>
          <Grid xs={12}>
            <TextField
              id="usagePurpose"
              label="電話番号(ハイフン不要)"
              value={phoneNumber}
              sx={{ width: '100%', textAlign: 'left' }}
              InputLabelProps={{
                shrink: true
              }}
              inputProps={{
                maxLength: 11
              }}
              error={phoneNumberError}
              helperText={phoneNumberError ? '半角数字のみ入力してください' : ''}
              onChange={(event) => {
                setPhoneNumber(event.target.value);
                setPhoneNumberError(!validateNumber(event.target.value));
              }}
            />
          </Grid>
          <Grid xs={12}>
            <FormControl>
              <FormLabel id="usageContent">利用内容</FormLabel>
              <RadioGroup
                value={usageContent}
                onChange={(event) => {
                  setUsageContent(event.target.value);
                }}
              >
                <FormControlLabel value="amateur" control={<Radio />} label="アマチュアスポーツ" />
                <FormControlLabel value="others" control={<Radio />} label="上記以外の利用" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid xs={12}>
            <FormControl>
              <FormLabel id="entranceFee">入場料</FormLabel>
              <RadioGroup
                value={entranceFee ? 'receive' : 'notreceive'}
                onChange={(event) => {
                  setEntranceFee(event.target.value === 'receive');
                }}
              >
                <FormControlLabel value="notreceive" control={<Radio />} label="徴収しない" />
                <FormControlLabel value="receive" control={<Radio />} label="徴収する" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid xs={12}>
            <FormControl>
              <FormLabel id="commercial">営利目的の利用か</FormLabel>
              <RadioGroup
                value={commercial ? 'purpose' : 'notpurpose'}
                onChange={(event) => {
                  setCommercial(event.target.value === 'purpose');
                }}
              >
                <FormControlLabel value="notpurpose" control={<Radio />} label="営利を目的としない" />
                <FormControlLabel value="purpose" control={<Radio />} label="営利を目的とする" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid xs={12}>
            <Typography>利用予定人数</Typography>
          </Grid>

          <Grid xs={12}>
            <Typography>小・中学生</Typography>
          </Grid>
          <Grid xs={6}>
            <Box display="flex" alignItems="center" width="100%">
              <TextField
                id="outlined-number"
                InputLabelProps={{
                  shrink: true
                }}
                fullWidth
                value={child}
                inputProps={{
                  inputMode: 'numeric',
                  style: { textAlign: 'right' }
                }}
                onFocus={(event) => {
                  event.target.select();
                }}
                onChange={(event) => {
                  setChild(toNumber(event.target.value));
                }}
              />
              <Typography>人</Typography>
            </Box>
          </Grid>
          <Grid xs={6} />

          <Grid xs={12}>
            <Typography>高校生</Typography>
          </Grid>
          <Grid xs={6}>
            <Box display="flex" alignItems="center" width="100%">
              <TextField
                id="outlined-number"
                InputLabelProps={{
                  shrink: true
                }}
                fullWidth
                value={middle}
                inputProps={{
                  inputMode: 'numeric',
                  style: { textAlign: 'right' }
                }}
                onFocus={(event) => {
                  event.target.select();
                }}
                onChange={(event) => {
                  setMiddle(toNumber(event.target.value));
                }}
              />
              <Typography>人</Typography>
            </Box>
          </Grid>
          <Grid xs={6} />

          <Grid xs={12}>
            <Typography>一般</Typography>
          </Grid>
          <Grid xs={6}>
            <Box display="flex" alignItems="center" width="100%">
              <TextField
                id="outlined-number"
                InputLabelProps={{
                  shrink: true
                }}
                fullWidth
                value={general}
                inputProps={{
                  inputMode: 'numeric',
                  style: { textAlign: 'right' }
                }}
                onFocus={(event) => {
                  event.target.select();
                }}
                onChange={(event) => {
                  setGeneral(toNumber(event.target.value));
                }}
              />
              <Typography>人</Typography>
            </Box>
          </Grid>
          <Grid xs={6} />

          <Grid xs={12}>
            <Divider />
          </Grid>

          <Grid xs={12}>
            <Typography>合計</Typography>
          </Grid>
          <Grid xs={6}>
            <Box display="flex" alignItems="center" width="100%">
              <TextField
                id="outlined-number"
                disabled
                InputLabelProps={{
                  shrink: true
                }}
                fullWidth
                value={sum}
                inputProps={{
                  inputMode: 'numeric',
                  style: { textAlign: 'right' }
                }}
              />
              <Typography>人</Typography>
            </Box>
          </Grid>
          <Grid xs={6} />

          <Grid xs={12}>
            <Typography>観覧者</Typography>
          </Grid>
          <Grid xs={6}>
            <Box display="flex" alignItems="center" width="100%">
              <TextField
                id="outlined-number"
                InputLabelProps={{
                  shrink: true
                }}
                fullWidth
                value={spectator}
                inputProps={{
                  inputMode: 'numeric',
                  style: { textAlign: 'right' }
                }}
                onFocus={(event) => {
                  event.target.select();
                }}
                onChange={(event) => {
                  setSpectator(toNumber(event.target.value));
                }}
              />
              <Typography>人</Typography>
            </Box>
          </Grid>
          <Grid xs={6} />

          <Grid xs={12}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => confirm('equipmentrequest')}
              disabled={
                !usagePurpose ||
                !usageType ||
                !usageDetails ||
                !groupName ||
                !representativeName ||
                !mailAddress ||
                !phoneNumber ||
                mailAddressError ||
                phoneNumberError
              }
            >
              備品・設備追加
            </Button>
          </Grid>

          <Grid xs={12}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => confirm('reservationconfirm')}
              disabled={
                !usagePurpose ||
                !usageType ||
                !usageDetails ||
                !groupName ||
                !representativeName ||
                !mailAddress ||
                !phoneNumber ||
                mailAddressError ||
                phoneNumberError
              }
            >
              次の画面へ進む
            </Button>
          </Grid>
        </Grid>
      </Box>
    </PageBase>
  );
};

export default UsagePurposeRegisterPage;
